import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { callGetMyRoles } from "api/MuyeonApi";
import useBodyScrollLock from "hooks/useBodyScrollLock";
import { openMemberType } from "util/openMemberType";

/**
 * 역할 인증 승인 안내 팝업 (전역 오버레이).
 *  - 관리자 승인 → 푸시(data.url = /home?roleApproved=SPACE) 클릭 시 홈(메인탭) 위로 팝업이 올라온다.
 *  - DefaultLayout 에서 항상 렌더하되, ?roleApproved= 쿼리가 있을 때만 노출(그 외 null).
 *  - 기존 CommonModal 과 동일한 하단시트(modal-bottom) 마크업/CSS 재사용.
 */
const ROLE_INFO = {
  SPACE:   { label: "공간 보유자",   noun: "공간",          create: "/spaces/create" },
  ACADEMY: { label: "학원 원장",     noun: "강사채용 공고", create: "/jobs/create" },
  TEAM:    { label: "공연팀 기획자", noun: "캐스팅 공고",   create: "/casting/create" },
  // 강사 — 인증 대기 대타공고가 있었다면 승인과 함께 자동 공개된다(서버). 다음 할 일은 대타공고 등록.
  TEACHER: { label: "강사",          noun: "대타공고",      create: "/subs/create" },
};

export default function RoleApproved() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const role = (params.get("roleApproved") || "").toUpperCase();
  const info = ROLE_INFO[role];
  useBodyScrollLock(!!role);

  // 방금 승인된 역할이 클라이언트(localStorage)에 아직 없으므로 서버에서 최신 역할을 재조회.
  // → 등록 페이지 진입 가드(can("space.create") 등)가 통과되도록 memberTypes 갱신.
  useEffect(() => {
    if (!role) return;
    callGetMyRoles()
      .then((r) => {
        const held = r?.data?.held;
        if (Array.isArray(held)) updateUser({ memberTypes: held });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  if (!role) return null;

  // 팝업만 닫기(현재 페이지 유지) — 쿼리 파라미터 제거
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("roleApproved");
    setParams(next, { replace: true });
  };

  // 다른 페이지로 이동 — 이동 전에 현재(홈) 히스토리 항목의 roleApproved 를 제거(replace).
  //  이렇게 안 하면 등록페이지에서 '뒤로가기' 시 /home?roleApproved= 로 돌아와 팝업이 다시 뜬다.
  const goTo = (path) => {
    const next = new URLSearchParams(params);
    next.delete("roleApproved");
    setParams(next, { replace: true });
    navigate(path);
  };

  return (
    <div data-id="modal-bottom" className="modal-bottom-dimmed is-show">
      <div className="modal-bottom">
        <div className="tit-box">
          <p>🎉 인증 승인 완료</p>
          <button onClick={close} type="button" className="ico-btn">
            <i className="close"></i>
            <span className="visually-hidden"> 닫기 </span>
          </button>
        </div>

        {info ? (
          <>
            <div className="con-box">
              <p className="t-body">{info.label} 인증이 승인됐어요.</p>
              <p className="t-side">이제 {info.noun}을(를) 등록할 수 있어요. 지금 등록하러 가실까요?</p>
            </div>
            <div className="btn-group">
              <button type="button" onClick={close} className="btn-md outlined">취소</button>
              <button type="button" onClick={() => goTo(info.create)} className="btn-md filled primary">등록하러 가기</button>
            </div>
          </>
        ) : (
          <>
            <div className="con-box">
              <p className="t-body">회원유형 인증이 승인됐어요.</p>
              <p className="t-side">회원유형 설정 화면에서 확인해 주세요.</p>
            </div>
            <div className="btn-group">
              <button type="button" onClick={() => { goTo("/home"); openMemberType(); }} className="btn-md filled primary">회원유형 설정으로 가기</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
